import { useRef, useState } from "react"
import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from "@mui/material"
import ArrowIcon from "@mui/icons-material/ArrowDropDown"

/**
 * One button for the thing people want, a menu for the rest.
 *
 * ── Why not three buttons, or one menu ──────────────────────────────────────
 *
 * Export is nearly always CSV. Three buttons makes the common case cost a
 * decision it does not deserve, and a plain menu makes it cost a click it does
 * not deserve either. A split button is the shape of "usually this, sometimes
 * one of those".
 *
 * ── Why the default does not follow the last choice ─────────────────────────
 *
 * Tempting, and wrong: a primary action that changes based on something you did
 * last week is one nobody can press without reading first, which is the entire
 * cost the pattern exists to avoid.
 */
export default function SplitButton({ options, disabled, startIcon, ...rest }) {
  const [open, setOpen] = useState(false)
  const anchor = useRef(null)
  const [primary, ...others] = options

  if (!primary) return null

  return (
    <>
      <ButtonGroup variant="outlined" color="inherit" ref={anchor} disabled={disabled} {...rest}>
        <Button startIcon={startIcon} onClick={primary.run}>
          {primary.label}
        </Button>
        {others.length > 0 ? (
          <Button
            size="small"
            onClick={() => setOpen((current) => !current)}
            aria-label="More export options"
            aria-haspopup="menu"
            aria-expanded={open}
            sx={{ px: 0.5, minWidth: 32 }}
          >
            <ArrowIcon />
          </Button>
        ) : null}
      </ButtonGroup>

      <Popper open={open} anchorEl={anchor.current} transition placement="bottom-end" sx={{ zIndex: 1300 }}>
        {({ TransitionProps }) => (
          /* Grown from the corner it came from, so it reads as belonging to the
             button rather than appearing from nowhere. */
          <Grow {...TransitionProps} style={{ transformOrigin: "right top" }}>
            <Paper elevation={8} sx={{ mt: 0.5, borderRadius: 2, minWidth: 200 }}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList dense autoFocusItem={open}>
                  {others.map((option) => (
                    <MenuItem
                      key={option.label}
                      onClick={() => {
                        setOpen(false)
                        option.run()
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}
